import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaClock, FaEye, FaGlobeAmericas, FaLock } from 'react-icons/fa';

const RecentlyViewedTab = () => {
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  useEffect(() => {
    const loadRecentlyViewed = () => {
      try {
        const stored = localStorage.getItem('recentlyViewedCases');
        if (stored) {
          const parsed = JSON.parse(stored);
          // Filter out any cases that are older than 30 days
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          const filtered = parsed.filter(item => {
            const viewedDate = new Date(item.viewedAt);
            return viewedDate > thirtyDaysAgo;
          });
          
          setRecentlyViewed(filtered);
          
          // Update localStorage if we filtered anything
          if (filtered.length !== parsed.length) {
            localStorage.setItem('recentlyViewedCases', JSON.stringify(filtered));
          }
        }
      } catch (error) {
        console.error('Error loading recently viewed cases:', error);
        setRecentlyViewed([]);
      }
    };

    loadRecentlyViewed();
    
    // Listen for localStorage changes in other tabs
    const handleStorageChange = (e) => {
      if (e.key === 'recentlyViewedCases') {
        loadRecentlyViewed();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const formatDetailedTime = (dateString) => {
    const viewedDate = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - viewedDate) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInMinutes < 5) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
    }
    return viewedDate.toLocaleDateString();
  };

  const formatCreatedDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const clearRecentlyViewed = () => {
    localStorage.removeItem('recentlyViewedCases');
    setRecentlyViewed([]);
  };

  return (
    <div className="tab-content recently-viewed-tab">
      <div className="tab-header">
        <h2>Recently Viewed Cases</h2>
        {recentlyViewed.length > 0 && (
          <button 
            onClick={clearRecentlyViewed}
            className="clear-recent-btn"
            title="Clear all recently viewed cases"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="recently-viewed-content">
        {recentlyViewed.length === 0 ? (
          <div className="empty-state">
            <FaEye className="empty-icon" />
            <h3>No Recently Viewed Cases</h3>
            <p>Cases you visit will appear here for quick access.</p>
            <p>Your viewing history is stored locally and automatically expires after 30 days.</p>
          </div>
        ) : (
          <>
            <div className="recent-stats">
              <p>
                <FaClock /> You've viewed <strong>{recentlyViewed.length}</strong> case{recentlyViewed.length !== 1 ? 's' : ''} recently
              </p>
            </div>
            
            <ul className="recent-cases-list">
              {recentlyViewed.map((item) => (
                <li key={item.id} className="recent-case-item">
                  <div className="case-info">
                    <div className="case-header">
                      <Link 
                        to={`/casefile/${item.id}`}
                        className="case-title"
                        title={item.title}
                      >
                        {item.title}
                      </Link>
                      <div className="case-badges">
                        {item.isPublic ? (
                          <span className="visibility-badge public" title="Public case">
                            <FaGlobeAmericas /> Public
                          </span>
                        ) : (
                          <span className="visibility-badge private" title="Private case">
                            <FaLock /> Private
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="case-metadata">
                      <div className="metadata-item">
                        <span className="metadata-label">Last viewed:</span>
                        <span className="metadata-value time-emphasis">
                          {formatDetailedTime(item.viewedAt)}
                        </span>
                      </div>
                      
                      <div className="metadata-item">
                        <span className="metadata-label">Created:</span>
                        <span className="metadata-value">
                          {formatCreatedDate(item.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="case-actions">
                    <Link 
                      to={`/casefile/${item.id}`}
                      className="view-case-btn"
                      title="View case details"
                    >
                      View Case
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
            
            <div className="recent-footer">
              <p className="footer-note">
                📝 <strong>Privacy Note:</strong> Recently viewed cases are stored locally in your browser only. 
                This data is not shared and automatically expires after 30 days.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecentlyViewedTab; 